import { useCallback, useMemo, useRef, useState } from "react";
import { fetchSubscriptionPromoQuote } from "../actions/global";
import {
  adaptSubscriptionPromoQuote,
  getPromoErrorCode,
  getPromoErrorTranslationKey,
  MAX_PROMO_CODE_LENGTH,
  normalizePromoCode,
  PROMO_ERROR_CODES,
} from "../utils/subscriptionPromo";

const IDLE_STATE = {
  status: "idle",
  errorKey: null,
};

export const useSubscriptionPromoCode = ({
  dispatch,
  authenticated = false,
} = {}) => {
  const requestIdRef = useRef(0);
  const [draftCode, setDraftCode] = useState("");
  const [quote, setQuote] = useState(null);
  const [feedback, setFeedback] = useState(IDLE_STATE);

  const clear = useCallback(() => {
    requestIdRef.current += 1;
    setDraftCode("");
    setQuote(null);
    setFeedback(IDLE_STATE);
  }, []);

  const changeDraftCode = useCallback((value) => {
    requestIdRef.current += 1;
    setDraftCode(String(value || "").slice(0, MAX_PROMO_CODE_LENGTH));
    setQuote(null);
    setFeedback(IDLE_STATE);
  }, []);

  const apply = useCallback(
    async ({ email = "", authenticated: authenticatedOverride } = {}) => {
      const code = normalizePromoCode(draftCode);
      const quoteAuthenticated =
        typeof authenticatedOverride === "boolean"
          ? authenticatedOverride
          : authenticated;

      if (!code) {
        clear();
        return { ok: false, empty: true };
      }

      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;
      setFeedback({ status: "checking", errorKey: null });

      try {
        const rawQuote = await dispatch(
          fetchSubscriptionPromoQuote(code, {
            authenticated: quoteAuthenticated,
            email,
          })
        );

        if (requestId !== requestIdRef.current) {
          return { ok: false, stale: true };
        }

        const nextQuote = adaptSubscriptionPromoQuote(rawQuote, {
          authenticated: quoteAuthenticated,
        });
        setDraftCode(nextQuote.code);
        setQuote(nextQuote);
        setFeedback({ status: "applied", errorKey: null });
        return { ok: true, quote: nextQuote };
      } catch (error) {
        if (requestId !== requestIdRef.current) {
          return { ok: false, stale: true };
        }

        setQuote(null);
        setFeedback({
          status: "error",
          errorKey: getPromoErrorTranslationKey(error, {
            missingEmail:
              !quoteAuthenticated && !String(email || "").trim(),
          }),
        });
        return { ok: false, error };
      }
    },
    [authenticated, clear, dispatch, draftCode]
  );

  const validateForEmail = useCallback(
    async (email) => {
      if (!quote) {
        return { ok: !normalizePromoCode(draftCode), quote: null };
      }

      if (authenticated) {
        return { ok: true, quote };
      }

      return apply({ email });
    },
    [apply, authenticated, draftCode, quote]
  );

  const reportUnapplied = useCallback(() => {
    if (!normalizePromoCode(draftCode) || quote) {
      return false;
    }

    setFeedback({
      status: "error",
      errorKey: "subscription.promo.errors.applyFirst",
    });
    return true;
  }, [draftCode, quote]);

  const handleCheckoutError = useCallback((error) => {
    requestIdRef.current += 1;
    const promoErrorCode = getPromoErrorCode(error);
    const eligibilityRejected =
      Boolean(promoErrorCode) &&
      promoErrorCode !== PROMO_ERROR_CODES.RATE_LIMITED;

    if (eligibilityRejected) {
      setQuote(null);
    }

    setFeedback({
      status: "error",
      errorKey: promoErrorCode
        ? getPromoErrorTranslationKey(error)
        : "subscription.promo.errors.checkout",
    });

    return promoErrorCode;
  }, []);

  const derived = useMemo(
    () => ({
      appliedCode: quote?.code || "",
      hasUnappliedDraft:
        Boolean(normalizePromoCode(draftCode)) && !Boolean(quote),
      isChecking: feedback.status === "checking",
      isApplied: feedback.status === "applied" && Boolean(quote),
    }),
    [draftCode, feedback.status, quote]
  );

  return {
    draftCode,
    quote,
    status: feedback.status,
    errorKey: feedback.errorKey,
    ...derived,
    apply,
    changeDraftCode,
    clear,
    handleCheckoutError,
    reportUnapplied,
    validateForEmail,
  };
};

export default useSubscriptionPromoCode;
